<template>
  <modal :show="showModal" modal-classes="modal-secondary" size="xl">
    <h3 class="mb-1">
      {{
        $t("ESTABLISHMENT_INTERNAL_ORDERS.GENERATE_INVOICE_FOR_DAY", {
          day: dayLabel,
        })
      }}
    </h3>
    <p class="text-muted">
      {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.GENERATE_INVOICE_MODAL_HELP") }}
    </p>

    <div class="table-responsive">
      <table class="table table-sm align-items-center">
        <thead class="thead-light">
          <tr>
            <th>{{ $t("COMMON.PRODUCT") }}</th>
            <th class="text-right">{{ $t("COMMON.UNIT_PRICE") }}</th>
            <th class="text-right">{{ $t("ESTABLISHMENT_INTERNAL_ORDERS.ORDERED") }}</th>
            <th class="text-right">
              {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.REMAINING") }}
            </th>
            <th class="text-right" style="min-width: 140px">
              {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.RECEIVED") }} (*)
            </th>
            <th class="text-right">{{ $t("COMMON.TOTAL") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="line in lines" :key="line.orderItemId">
            <td>
              {{ line.name }}
              <div class="text-muted small">{{ line.code }}</div>
            </td>
            <td class="text-right">{{ $formatCurrency(line.unitPrice, 4) }}</td>
            <td class="text-right">{{ line.ordered }}</td>
            <td class="text-right">{{ line.remaining }}</td>
            <td>
              <base-input
                v-model="quantities[line.orderItemId]"
                type="number"
                min="0"
                step="0.01"
                :placeholder="String(line.remaining)"
                input-classes="form-control-alternative text-right"
              />
            </td>
            <td class="text-right">
              {{ $formatCurrency(lineTotal(line)) }}
            </td>
          </tr>
          <tr v-if="!lines.length">
            <td colspan="6" class="text-center text-muted">
              {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.NOTHING_LEFT_TO_INVOICE") }}
            </td>
          </tr>
        </tbody>
        <tfoot v-if="lines.length">
          <tr>
            <th colspan="5" class="text-right">{{ $t("COMMON.TOTAL") }}</th>
            <th class="text-right">{{ $formatCurrency(total) }}</th>
          </tr>
        </tfoot>
      </table>
    </div>

    <div class="alert alert-info" v-if="hasOverReception">
      <i class="fas fa-info-circle mr-1"></i>
      {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.OVER_RECEPTION_HINT") }}
    </div>

    <!-- Nothing received at all: the day is closed by an empty invoice, which
         must say why before the production accepts it. -->
    <div v-if="isNonReception">
      <div class="alert alert-warning">
        <i class="fas fa-exclamation-triangle mr-1"></i>
        {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.NOT_RECEIVED_HINT") }}
      </div>

      <base-input :label="`${$t('INTERNAL_DOCUMENTS.NON_RECEPTION_REASON')} (*)`">
        <el-select
          v-model="reasonCode"
          :placeholder="$t('INTERNAL_DOCUMENTS.REASON_PLACEHOLDER')"
          class="w-100"
        >
          <el-option
            v-for="code in nonReceptionReasons"
            :key="code"
            :value="code"
            :label="$t(`INTERNAL_DOCUMENTS.NON_RECEPTION_REASONS.${code}`)"
          />
        </el-select>
      </base-input>

      <base-input :label="reasonDetailLabel">
        <textarea
          v-model="reason"
          class="form-control"
          rows="2"
          :placeholder="$t('INTERNAL_DOCUMENTS.REASON_DETAIL_PLACEHOLDER')"
        ></textarea>
      </base-input>
    </div>

    <template slot="footer">
      <button
        type="button"
        class="btn btn-primary"
        :disabled="loading || !lines.length || !canGenerate"
        @click="generate"
      >
        <span v-if="loading" class="btn-inner--icon">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
        {{
          isNonReception
            ? $t("ESTABLISHMENT_INTERNAL_ORDERS.DECLARE_NOT_RECEIVED")
            : $t("ESTABLISHMENT_INTERNAL_ORDERS.GENERATE_INVOICE")
        }}
      </button>

      <button
        type="button"
        class="btn btn-link ml-auto"
        :disabled="loading"
        @click="closeModal"
      >
        {{ $t("COMMON.CLOSE") }}
      </button>
    </template>
  </modal>
</template>

<script>
import moment from "moment";
import { Select, Option } from "element-ui";
import requestErrorMixin from "@/mixins/request-error-mixin";

export default {
  name: "establishment-internal-order-generate-invoice-modal",

  components: {
    [Select.name]: Select,
    [Option.name]: Option,
  },

  mixins: [requestErrorMixin],

  props: {
    order: {
      type: Object,
      required: true,
    },
    day: {
      type: String,
      default: null,
    },
    showModal: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      // keyed by order line id, prefilled with what is left to invoice
      quantities: {},
      reasonCode: null,
      reason: null,
      nonReceptionReasons: [
        "OPENING_DELAYED",
        "CAFETERIA_CLOSED",
        "SCHOOL_CLOSED",
        "NOT_DELIVERED",
        "CANCELED_WITH_PRODUCTION",
        "ORDER_ERROR",
        "OTHER",
      ],
    };
  },

  computed: {
    dayLabel() {
      return this.day ? moment(this.day).format("ddd DD/MM") : "";
    },
    state() {
      return this.order.invoicing_items_state || {};
    },
    /**
     * The lines of that day still carrying something to invoice, with the ordered
     * quantity next to the received one so the two can be compared.
     */
    lines() {
      if (!this.day) return [];

      const ordered = this.state.orderedItems || {};
      const remaining = this.state.remainingItems || {};
      const itemsById = {};
      (this.order.items || []).forEach((item) => {
        itemsById[String(item.id)] = item;
      });

      return Object.keys(ordered)
        .filter((key) => ordered[key].delivery_date === this.day)
        .map((key) => {
          const item = itemsById[key];
          return {
            orderItemId: key,
            name: item?.storable?.name || `#${ordered[key].storable_id}`,
            code: item?.storable?.code || "",
            unitPrice: Number(ordered[key].unit_price || 0),
            ordered: Number(ordered[key].quantity || 0),
            remaining: Number(remaining[key]?.quantity || 0),
          };
        })
        .filter((line) => line.remaining > 0)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    total() {
      return this.lines.reduce((carry, line) => carry + this.lineTotal(line), 0);
    },
    hasSomethingToInvoice() {
      return this.lines.some((line) => Number(this.quantities[line.orderItemId]) > 0);
    },
    /**
     * Every line explicitly set to 0: nothing was received that day. An empty
     * field is not a 0 — it is still being typed.
     */
    isNonReception() {
      return (
        this.lines.length > 0 &&
        this.lines.every((line) => {
          const value = this.quantities[line.orderItemId];
          return value !== "" && value !== null && value !== undefined && Number(value) === 0;
        })
      );
    },
    /** « Autre » ne dit rien à lui seul : la précision devient obligatoire. */
    reasonDetailRequired() {
      return this.reasonCode === "OTHER";
    },
    reasonDetailLabel() {
      const label = this.$t("INTERNAL_DOCUMENTS.REASON_DETAIL");
      return this.reasonDetailRequired ? `${label} (*)` : label;
    },
    canGenerate() {
      if (this.hasSomethingToInvoice) return true;
      if (!this.isNonReception || !this.reasonCode) return false;
      return !this.reasonDetailRequired || !!(this.reason || "").trim();
    },
    hasOverReception() {
      return this.lines.some(
        (line) => Number(this.quantities[line.orderItemId] || 0) > line.remaining
      );
    },
  },

  watch: {
    showModal(value) {
      if (value) {
        this.prefill();
      }
    },
    day() {
      if (this.showModal) {
        this.prefill();
      }
    },
  },

  methods: {
    /**
     * Prefill with what is left to invoice: the common case is a delivery matching
     * the order, so the user only edits the lines that differ.
     */
    prefill() {
      const quantities = {};
      this.lines.forEach((line) => {
        quantities[line.orderItemId] = line.remaining;
      });
      this.quantities = quantities;
      // Reopening the modal must never offer the reason of a previous day.
      this.reasonCode = null;
      this.reason = null;
    },

    lineTotal(line) {
      const quantity = Number(this.quantities[line.orderItemId] || 0);
      return quantity > 0 ? quantity * line.unitPrice : 0;
    },

    async generate() {
      // The invoiced day is what dates the invoice: never post without it.
      if (!this.day) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.INTERNAL_ORDER_DELIVERY_DATE_REQUIRED"),
        });
        return;
      }

      const items = {};
      this.lines.forEach((line) => {
        const quantity = Number(this.quantities[line.orderItemId]);
        if (!isNaN(quantity) && quantity >= 0) {
          items[line.orderItemId] = { quantity };
        }
      });

      this.loading = true;
      try {
        const invoice = await this.$store.dispatch(
          "establishmentInternalOrders/generateInternalInvoice",
          {
            id: this.order.id,
            deliveryDate: this.day,
            items,
            reasonCode: this.isNonReception ? this.reasonCode : null,
            reason: this.isNonReception ? (this.reason || "").trim() || null : null,
          }
        );
        this.$notify({
          type: "success",
          message: this.$t("ESTABLISHMENT_INTERNAL_ORDERS.INVOICE_GENERATED"),
        });
        this.$emit("generated", invoice);
        this.closeModal();

        if (invoice && invoice.id) {
          this.$router.push({
            name: "View EstablishmentInternalInvoice",
            params: { id: invoice.id },
          });
        }
      } catch (error) {
        // The API answers with explicit codes (DAY_ALREADY_INVOICED,
        // REMAINING_ITEMS_EMPTY_FOR_DAY, ...) carried in `detail`.
        await this.showRequestError(error);
      } finally {
        this.loading = false;
      }
    },

    closeModal() {
      this.$emit("onCloseModal");
    },
  },
};
</script>
